import React, { useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { useDisclosure } from '@chakra-ui/react'
import { get } from 'lodash'

import {
  TopicElementModelAsEditor,
  TopicModelAsEditor,
  TopicModelAsMember
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { EditElementWrapper } from '../EditElementWrapper'
import LinkElement from '../../topic-elements/LinkElement'
import {
  ButtonOutline,
  ButtonPrimary,
  Modal,
  Box
} from 'front-end/src/components'
import { IEmbedlyResult } from 'types/Embedly'
import { useTopicForEditQuery } from '../../../topic-edit-page/TopicEditPage'
import { useSetLinkElementUrlMutation } from './EditLinkElement.codegen'

const EditLinkElement = (props: {
  isMultiPage?: boolean
  element: TopicElementModelAsEditor
  topic: TopicModelAsMember | TopicModelAsEditor
}) => {
  const { element } = props
  const content = element.data?.content as IEmbedlyResult | null

  const { refetch: refetchTopic } = useTopicForEditQuery()

  const [editing, setEditing] = useState(!content)

  const { isOpen, onClose, onOpen } = useDisclosure({
    defaultIsOpen: !!(content?.url && !content)
  })

  const [link, setLink] = useState(content ? content.url : '')
  const [setLinkElementUrl, { loading }] = useSetLinkElementUrlMutation({
    onCompleted: (result) => {
      const hasLinkBeenSet = get(
        result,
        'topic.draftVersion.element.setLinkUrl',
        null
      )
      if (!hasLinkBeenSet) {
        onOpen()
        return
      }
      setEditing(false)
    }
  })

  const extractContent = async () => {
    await setLinkElementUrl({
      variables: {
        topicId: props.topic.id,
        elementId: element.id,
        url: link,
        getEmbedly: true
      }
    })

    refetchTopic()
  }

  return (
    <EditElementWrapper {...props}>
      {!editing ? (
        <LinkElement {...element} activateEditing={() => setEditing(true)} />
      ) : (
        <div className="link-edit">
          <label className="link-label" htmlFor="link-input">
            <Trans>Add a web link (URL)</Trans>:
          </label>
          <div className="link-edit__controls">
            <input
              value={link}
              id="link-input"
              type="text"
              onChange={(e) => setLink(e.target.value)}
              placeholder="http://www.yourlink.com"
            />
            <ButtonPrimary
              disabled={!link}
              isLoading={loading}
              onClick={extractContent}
            >
              <Trans>Save</Trans>
            </ButtonPrimary>
          </div>
        </div>
      )}

      <Modal
        show={isOpen}
        header={t`Failed to retrieve link`}
        controls={[
          <ButtonOutline
            key={1}
            onClick={() => {
              onClose()
            }}
          >
            <Trans>Cancel</Trans>
          </ButtonOutline>,
          <ButtonPrimary
            isLoading={loading}
            key={2}
            onClickAsync={async () => {
              await setLinkElementUrl({
                variables: {
                  topicId: props.topic.id,
                  elementId: element.id,
                  url: link,
                  getEmbedly: false
                }
              })
              onClose()
              setEditing(false)
              refetchTopic()
            }}
          >
            <Trans>Use this URL without preview</Trans>
          </ButtonPrimary>
        ]}
      >
        <Box mb="16px">
          <Trans>
            We were unable to get a preview of the link. Most likely it’s a
            private link or page where embedding is forbidden.
          </Trans>
        </Box>
      </Modal>
    </EditElementWrapper>
  )
}

export default EditLinkElement
